import React, { createContext, useContext, ReactNode } from 'react';
import toast, { Toaster, ToastBar } from 'react-hot-toast';

interface NotificationContextType {
  showNotification: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showInfo: (message: string) => void;
  showWarning: (message: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const TOAST_THEME = {
  success: {
    shell: 'border-emerald-200/90 bg-emerald-50/95 text-emerald-900 dark:border-emerald-800/60 dark:bg-emerald-950/60 dark:text-emerald-100',
    icon: 'text-emerald-600 dark:text-emerald-300',
    close: 'hover:bg-emerald-200/60 dark:hover:bg-emerald-900/60',
    label: 'Success',
  },
  error: {
    shell: 'border-rose-200/90 bg-rose-50/95 text-rose-900 dark:border-rose-800/60 dark:bg-rose-950/60 dark:text-rose-100',
    icon: 'text-rose-600 dark:text-rose-300',
    close: 'hover:bg-rose-200/60 dark:hover:bg-rose-900/60',
    label: 'Error',
  },
  warning: {
    shell: 'border-amber-200/90 bg-amber-50/95 text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/60 dark:text-amber-100',
    icon: 'text-amber-600 dark:text-amber-300',
    close: 'hover:bg-amber-200/60 dark:hover:bg-amber-900/60',
    label: 'Warning',
  },
  info: {
    shell: 'border-sky-200/90 bg-sky-50/95 text-sky-900 dark:border-sky-800/60 dark:bg-sky-950/60 dark:text-sky-100',
    icon: 'text-sky-600 dark:text-sky-300',
    close: 'hover:bg-sky-200/60 dark:hover:bg-sky-900/60',
    label: 'Info',
  },
} as const;

function toastTypeToVariant(type: string): keyof typeof TOAST_THEME {
  if (type === 'success') return 'success';
  if (type === 'error') return 'error';
  if (type === 'loading') return 'info';
  return 'info';
}

const ToastInlineActions: React.FC<{
  toastId: string;
  message: React.ReactNode;
  type: string;
}> = ({ toastId, message, type }) => {
  const [expanded, setExpanded] = React.useState(false);
  const variant = TOAST_THEME[toastTypeToVariant(type)];
  const messageText = typeof message === 'string' ? message : '';
  const hasDetails = messageText.length > 120 || messageText.includes(';') || messageText.includes('\n');

  return (
    <div
      className={[
        'relative w-full max-w-md rounded-lg border px-3 py-2.5 shadow-md',
        variant.shell,
      ].join(' ')}
      role={type === 'error' ? 'alert' : 'status'}
    >
      <div className="flex items-start gap-2">
        <span className={['mt-0.5 shrink-0', variant.icon].join(' ')}>
          {type === 'error' ? '⚠' : type === 'success' ? '✓' : type === 'warning' ? '!' : 'i'}
        </span>
        <div className="min-w-0 flex-1">
          <div
            className={[
              'text-sm leading-snug break-words [overflow-wrap:anywhere]',
              !expanded ? 'line-clamp-2' : '',
            ].join(' ')}
          >
            {message}
          </div>
          <div className="mt-2 flex items-center gap-2">
            {hasDetails ? (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="rounded px-1.5 py-0.5 text-xs font-medium underline underline-offset-2 opacity-90 hover:opacity-100"
              >
                {expanded ? 'Hide details' : 'Details'}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => toast.dismiss(toastId)}
              className={['rounded px-1.5 py-0.5 text-xs font-medium', variant.close].join(' ')}
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const showNotification = (
    message: string,
    type: 'success' | 'error' | 'info' | 'warning' = 'info'
  ) => {
    switch (type) {
      case 'success':
        toast.success(message, {
          duration: 4000,
          position: 'top-right',
        });
        break;
      case 'error':
        toast.error(message, {
          duration: 5000,
          position: 'top-right',
        });
        break;
      case 'warning':
        toast(message, {
          icon: '⚠️',
          duration: 4000,
          position: 'top-right',
          style: {
            background: '#fbbf24',
            color: '#fff',
          },
        });
        break;
      case 'info':
      default:
        toast(message, {
          duration: 4000,
          position: 'top-right',
        });
        break;
    }
  };

  const showSuccess = (message: string) => showNotification(message, 'success');
  const showError = (message: string) => showNotification(message, 'error');
  const showInfo = (message: string) => showNotification(message, 'info');
  const showWarning = (message: string) => showNotification(message, 'warning');

  return (
    <NotificationContext.Provider
      value={{
        showNotification,
        showSuccess,
        showError,
        showInfo,
        showWarning,
      }}
    >
      {children}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: { background: 'transparent', boxShadow: 'none', padding: 0 },
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <ToastInlineActions toastId={t.id} message={message} type={t.type} />
            )}
          </ToastBar>
        )}
      </Toaster>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

